import React, { useEffect } from "react";
import ChannelIcon from "./ChannelIcon";
import {
  getClusterByDisplayName,
  getAxisScoresForCluster
} from "../constants/topicClusters";

const ChannelTopicsDrawer = ({ isOpen, onClose, row }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !row) return null;

  const dominant = getClusterByDisplayName(row.topTopic);
  const dominantTopics = dominant
    ? (row.topics || []).filter((t) => t.cluster === dominant.id)
    : [];

  const dominantInternalVals = dominantTopics
    .map((t) => t.internalExternal)
    .filter((v) => typeof v === "number");
  const dominantOperationalVals = dominantTopics
    .map((t) => t.operationalStrategic)
    .filter((v) => typeof v === "number");

  const dominantAxisRange =
    dominantInternalVals.length > 0 && dominantOperationalVals.length > 0
      ? {
          internalExternalMin: Math.min(...dominantInternalVals),
          internalExternalMax: Math.max(...dominantInternalVals),
          operationalStrategicMin: Math.min(...dominantOperationalVals),
          operationalStrategicMax: Math.max(...dominantOperationalVals)
        }
      : null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 border-0 cursor-default"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside
        className="relative h-full w-full max-w-md bg-white-f border-l border-grey-20 shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="channel-topics-drawer-title"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-grey-20 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ChannelIcon isPrivate={row.is_private} size={18} />
              <h2
                id="channel-topics-drawer-title"
                className="text-lg font-medium text-black-0 truncate"
              >
                {row.channel}
              </h2>
            </div>
            <p className="text-xs text-grey-60 mt-2 leading-relaxed">
              Signed axis scores (-100..+100) show where each topic sits on
              the compass: Internal ↔ External (horizontal) and Operational ↔
              Strategic (vertical). Negative values lean Internal/Strategic;
              positive values lean External/Operational.
            </p>
            {typeof row.messagesInPeriod === "number" && row.messagesInPeriod > 0 && (
              <p className="text-xs text-grey-60 mt-2 leading-relaxed">
                Messages recorded in Cogniflock for this channel and date
                range:{" "}
                <span className="text-black-0 font-medium">
                  {row.messagesInPeriod}
                </span>
                . Per-topic numbers are AI-estimated counts from daily topic
                extraction, not a direct Slack message tally.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 rounded-md text-grey-60 hover:text-black-0 hover:bg-grey-10"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          <section>
            <h3 className="text-xs font-medium uppercase tracking-wide text-grey-60 mb-2">
              Dominant cluster
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {dominant && (
                <span
                  className="px-2 py-1 rounded-md text-xs font-medium text-white"
                  style={{ backgroundColor: dominant.color }}
                >
                  {row.topTopic} ({row.percentage}%)
                </span>
              )}
              {!dominant && (
                <span className="text-sm text-grey-60">{row.topTopic}</span>
              )}
            </div>
            {dominantAxisRange && (
              <div className="mt-3 text-sm text-grey-60 space-y-1">
                <div>
                  Internal ↔ External:{" "}
                  <span className="text-black-0 font-medium">
                    {dominantAxisRange.internalExternalMin}
                  </span>
                  {" to "}
                  <span className="text-black-0 font-medium">
                    {dominantAxisRange.internalExternalMax}
                  </span>
                </div>
                <div>
                  Operational ↔ Strategic:{" "}
                  <span className="text-black-0 font-medium">
                    {dominantAxisRange.operationalStrategicMin}
                  </span>
                  {" to "}
                  <span className="text-black-0 font-medium">
                    {dominantAxisRange.operationalStrategicMax}
                  </span>
                </div>
              </div>
            )}
          </section>

          <section>
            <h3 className="text-xs font-medium uppercase tracking-wide text-grey-60 mb-3">
              All topics ({row.topics?.length ?? 0})
            </h3>
            <ul className="space-y-3">
              {(row.topics || []).map((topic) => {
                const c = getClusterByDisplayName(topic.clusterDisplayName);
                const fallbackAxes = topic.cluster
                  ? getAxisScoresForCluster(topic.cluster)
                  : null;

                const xSigned =
                  typeof topic.internalExternal === "number"
                    ? topic.internalExternal
                    : fallbackAxes?.internalExternal;
                const ySigned =
                  typeof topic.operationalStrategic === "number"
                    ? topic.operationalStrategic
                    : fallbackAxes?.operationalStrategic;

                const axes =
                  typeof xSigned === "number" && typeof ySigned === "number"
                    ? {
                        internalExternal: xSigned,
                        operationalStrategic: ySigned,
                        estimatedAxis:
                          typeof topic.internalExternal !== "number" ||
                          typeof topic.operationalStrategic !== "number"
                      }
                    : null;
                const dotColor = c?.color ?? "#6B7280";
                return (
                  <li
                    key={`${topic.name}-${topic.cluster}`}
                    className="rounded-lg border border-grey-20 p-3 bg-white"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full shrink-0 mt-1.5"
                          style={{ backgroundColor: dotColor }}
                        />
                        <span className="text-sm font-medium text-black-0 break-words">
                          {topic.name}
                        </span>
                      </div>
                      <span className="text-sm text-grey-60 shrink-0">
                        {topic.percentage}%
                        {typeof topic.value === "number"
                          ? ` · ${topic.value} messages`
                          : ""}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span
                        className="text-[11px] px-2 py-0.5 rounded border border-grey-20 text-grey-60"
                        style={{ borderColor: `${dotColor}55` }}
                      >
                        {topic.clusterDisplayName}
                      </span>
                    </div>
                    {axes && (
                      <div className="mt-2 text-xs text-grey-60 space-y-0.5">
                        <div>
                          Internal ↔ External: {axes.internalExternal}
                          {axes.estimatedAxis ? " (estimated)" : ""}
                        </div>
                        <div>
                          Operational ↔ Strategic:{" "}
                          {axes.operationalStrategic}
                          {axes.estimatedAxis ? " (estimated)" : ""}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </aside>
    </div>
  );
};

export default ChannelTopicsDrawer;
